import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, subscribeToPage } from '../firebase/firebase-config';

function Syllabus() {
  const [content, setContent] = useState({ 
    session: '2025-26', 
    syllabusItems: [], 
    sliderImages: [],
    title: 'Syllabus'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getPageContent('syllabus');
        setContent(prev => ({ ...prev, ...data }));
      } catch (error) { 
        console.error(error); 
      } finally { 
        setLoading(false); 
      }
    };
    loadData();
    const unsubscribe = subscribeToPage('syllabus', (data) => { 
      if (data) setContent(prev => ({ ...prev, ...data })); 
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Syllabus...</p>
      </div>
    );
  }

  // Group syllabus items by class
  const syllabusItems = content.syllabusItems || [];
  
  // Sort items by class (Nursery, LKG, UKG, then Class 1-12)
  const getClassOrder = (className) => {
    const order = {
      'Nursery': 1, 'LKG': 2, 'UKG': 3
    };
    const match = className?.match(/Class (\d+)/);
    if (match) return 10 + parseInt(match[1]);
    return order[className] || 99;
  };

  const sortedItems = [...syllabusItems].sort((a, b) => {
    return getClassOrder(a.class) - getClassOrder(b.class);
  });

  return (
    <div className="Syllabus-page">
      {/* Hero Section - No banner */}
       <div className="container">
          <h1>{content.title}</h1>
          <div className="hero-divider"></div>
          <p>Session {content.session} • Class-wise Syllabus • Downloadable PDFs</p>
        </div>

      <div className="container">
        <div className="session-info">
          <div className="session-badge">
            📅 Academic Session: <strong>{content.session}</strong>
          </div>
        </div>

        <div className="syllabus-list">
          {sortedItems.length > 0 ? (
            sortedItems.map((item) => (
              <div key={item.id} className="syllabus-card">
                <div className="syllabus-class">{item.class}</div>
                <div className="syllabus-term">{item.term || 'Annual Syllabus'}</div>
                {item.file && item.file.url ? (
                  <a 
                    href={item.file.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="download-btn"
                  >
                    📥 Download Syllabus
                  </a>
                ) : (
                  <span className="not-available">Coming Soon</span>
                )}
              </div>
            ))
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No Syllabus Available</h3>
              <p>Syllabus will be uploaded soon. Please check back later.</p>
            </div>
          )}
        </div>

        <div className="note">
          <p>📌 This syllabus is applicable for the <strong>{content.session}</strong> session. 
          Any further updates or changes will be notified through the school portal and 
          communicated via the class teachers.</p>
        </div>

        {/* Back to Academics Link */}
        <div className="back-link">
          <Link to="/academics" className="back-btn">
            ← Back to Academics
          </Link>
        </div>
      </div>

      <style>{`
        .syllabus-page {
          background: #f8fafc;
          min-height: 100vh;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: #1a3a6b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px 60px;
        }

        /* Page Hero */
        .page-hero {
          background: linear-gradient(135deg, #1a3a6b, #f59e0b);
          color: white;
          text-align: center;
          padding: 60px 20px;
          margin-bottom: 40px;
        }

        .page-hero h1 {
          font-size: 48px;
          margin-bottom: 20px;
          font-weight: 700;
        }

        .hero-divider {
          width: 60px;
          height: 4px;
          background: white;
          margin: 0 auto 20px;
          border-radius: 2px;
        }

        .page-hero p {
          font-size: 18px;
          opacity: 0.9;
        }

        /* Session Info */
        .session-info {
          text-align: center;
          margin-bottom: 40px;
        }

        .session-badge {
          display: inline-block;
          background: white;
          padding: 10px 24px;
          border-radius: 40px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          font-size: 16px;
          color: #475569;
        }

        .session-badge strong {
          color: #1a3a6b;
          font-size: 18px;
        }

        /* Syllabus List */
        .syllabus-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 25px;
          margin-bottom: 40px;
        }

        .syllabus-card {
          background: white;
          border-radius: 20px;
          padding: 30px 25px;
          text-align: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s, box-shadow 0.3s;
          position: relative;
          overflow: hidden;
        }

        .syllabus-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        }

        .syllabus-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #1a3a6b, #f59e0b);
        }

        .syllabus-class {
          font-size: 24px;
          font-weight: 800;
          color: #1a3a6b;
          margin-bottom: 12px;
        }

        .syllabus-term {
          display: inline-block;
          background: #f59e0b;
          color: white;
          padding: 4px 16px;
          border-radius: 25px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 20px;
        }

        .download-btn {
          display: inline-block;
          background: #1a3a6b;
          color: white;
          padding: 12px 24px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s;
        }

        .download-btn:hover {
          background: #f59e0b;
          transform: translateY(-2px);
        }

        .not-available {
          color: #94a3b8;
          font-style: italic;
          font-size: 14px;
          padding: 10px;
          display: inline-block;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 20px;
        }

        .empty-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          color: #1a3a6b;
          margin-bottom: 10px;
        }

        .empty-state p {
          color: #64748b;
        }

        /* Note Section */
        .note {
          background: #fef3c7;
          border-radius: 16px;
          padding: 20px 30px;
          margin: 30px 0;
          border-left: 4px solid #f59e0b;
        }

        .note p {
          color: #92400e;
          font-size: 14px;
          line-height: 1.6;
          margin: 0;
        }

        /* Back Link */
        .back-link {
          text-align: center;
          margin-top: 20px;
        }

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #e2e8f0;
          color: #475569;
          padding: 12px 28px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s;
        }

        .back-btn:hover {
          background: #1a3a6b;
          color: white;
          transform: translateX(-5px);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .container {
            padding: 0 20px 50px;
          }
        }

        @media (max-width: 768px) {
          .page-hero {
            padding: 50px 20px;
          }
          
          .page-hero h1 {
            font-size: 32px;
          }
          
          .page-hero p {
            font-size: 16px;
          }
          
          .syllabus-list {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .syllabus-class {
            font-size: 20px;
          }
          
          .session-badge {
            font-size: 14px;
          }
        }

        @media (max-width: 480px) {
          .page-hero h1 {
            font-size: 28px;
          }
          
          .syllabus-card {
            padding: 25px 20px;
          }
          
          .note {
            padding: 15px 20px;
          }
        }
      `}</style>
    </div>
  );
}

export default Syllabus;